import { inject } from '@angular/core';
import { GameData, Language, PluginType } from '@meta-press/shared-plugins';
import { SettingsService } from '../../settings-service/settings.service';
import {
	IGameGenerationService,
	GenerationResult,
	GenerationRequest,
	GenerationPrompts,
	ValidationResult,
	GenerationStep,
} from './game-generation.interface';

/**
 * Base abstract class for all game generation services.
 * Provides common functionality like setting the content language.
 */
export abstract class BaseGenerationService<
	TGameData extends GameData = GameData,
> implements IGameGenerationService<TGameData>
{
	// Inject SettingsService to access current language settings
	protected settingsService = inject(SettingsService);

	// Abstract properties and methods that child classes must implement
	abstract readonly pluginType: PluginType;

	/**
	 * Core response processing that child classes implement.
	 * This contains the actual game-specific generation logic.
	 */
	protected abstract processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult;

	/**
	 * Abstract methods from IGameGenerationService that child classes must implement
	 */
	abstract createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts;
	abstract validateRequest(request: GenerationRequest): ValidationResult;
	abstract getDefaultParameters(): any;

	getDefaultBackgroundGenerationParameters(): any {
		return this.getDefaultParameters();
	}

	/**
	 * Get default generation steps for this plugin type.
	 * Default implementation wraps getDefaultParameters() in a single step.
	 * Override to implement multi-step generation (e.g., Study Material Summary)
	 * @returns Array of generation steps
	 */
	getDefaultSteps(): GenerationStep[] {
		return [
			{
				stepId: 'main',
				stepType: 'generate',
				parameters: this.getDefaultParameters(),
			},
		];
	}

	/**
	 * Get default steps for background generation.
	 * Default implementation wraps getDefaultBackgroundGenerationParameters() in a single step.
	 * Override for custom background behavior (e.g., CrosswordPuzzle uses optimization)
	 * @returns Array of generation steps optimized for background processing
	 */
	getDefaultBackgroundSteps(): GenerationStep[] {
		return [
			{
				stepId: 'main',
				stepType: 'generate',
				parameters: this.getDefaultBackgroundGenerationParameters(),
			},
		];
	}

	/**
	 * Final processResponse that wraps the core implementation.
	 * Automatically adds content language and ensures default properties are set.
	 */
	processResponse(response: any, parameters: any): GenerationResult {
		// Call the child class's core implementation
		const result = this.processResponseCore(response, parameters);

		// If generation was successful, ensure proper defaults and add content language
		if (result.success && result.gameData) {
			// Ensure essential GameData properties have proper default values
			this.ensureDefaultGameDataProperties(result.gameData, parameters);

			// Get current language from settings service
			const currentLang =
				this.settingsService.GetCurrentLanguageDefinition();
			if (currentLang) {
				// Set the content language using the short code
				result.gameData.contentLanguage =
					currentLang.shortCode as Language;
			} else {
				// Fallback to Finnish if no language is set
				result.gameData.contentLanguage = Language.FINNISH;
			}
		}

		return result;
	}

	/**
	 * Ensures essential GameData properties have proper default values.
	 * This prevents validation failures due to undefined essential properties.
	 * @param gameData The game data to ensure defaults for
	 * @param parameters The generation parameters used
	 */
	protected ensureDefaultGameDataProperties(
		gameData: GameData,
		parameters: any,
	): void {
		// Ensure selectedInterface has a valid default value

		// If parameters define selected interface, use it.
		if (
			typeof parameters.selectedInterfaceId === 'number' &&
			!isNaN(parameters.selectedInterfaceId)
		) {
			gameData.selectedInterfaceId = parameters.selectedInterfaceId;
		} else if (
			gameData.selectedInterfaceId === undefined ||
			gameData.selectedInterfaceId === null ||
			isNaN(gameData.selectedInterfaceId)
		) {
			// Default to undefined - the renderer will use the first available interface
		}

		// Ensure type is properly set to match the plugin type
		if (gameData.type === undefined || gameData.type === null) {
			gameData.type = this.pluginType;
			console.debug(
				`[BaseGenerationService] Set type default: ${gameData.type}`,
			);
		}

		// Ensure contentLanguage is not undefined (this is also handled in processResponse but as fallback)
		if (
			gameData.contentLanguage === undefined ||
			gameData.contentLanguage === null
		) {
			gameData.contentLanguage = ''; // Empty string for backward compatibility
			console.debug(
				`[BaseGenerationService] Set contentLanguage default: empty string`,
			);
		}

		console.debug(
			`[BaseGenerationService] GameData defaults ensured for type ${this.pluginType}:`,
			{
				selectedInterfaceId: gameData.selectedInterfaceId,
				type: gameData.type,
				contentLanguage: gameData.contentLanguage,
			},
		);
	}

	/**
	 * Optional method for merging content.
	 * Child classes can override this if they support content merging.
	 */
	mergeContent?(
		existingGameData: TGameData,
		newGameData: TGameData,
		clearPrevious: boolean,
	): TGameData;

	/**
	 * Enhance user prompt with concept analysis context when items are selected.
	 * This method is called automatically by GenerationUtils to add concept guidance.
	 * @param userPrompt The original user prompt
	 * @param selectedConceptItems Array of LLM directive strings from concept analysis
	 * @param materialHandlingInstructions Additional instructions for handling source material
	 * @returns Enhanced user prompt with concept context and material handling instructions
	 */
	enhanceUserPrompt(
		userPrompt: string,
		selectedConceptItems?: string[],
		materialHandlingInstructions?: string,
	): string {
		let enhancedPrompt = userPrompt;

		// Add concept analysis context if items are selected
		if (selectedConceptItems && selectedConceptItems.length > 0) {
			const conceptContext = `\n\nTärkeää! Etsi materiaalista erityisesti näiden asioiden kannalta: ${selectedConceptItems.join(', ')}`;
			enhancedPrompt += conceptContext;
		}

		// Add material handling instructions if provided
		if (
			materialHandlingInstructions &&
			materialHandlingInstructions.trim()
		) {
			const instructionsContext = `\n\nTärkeää! Ota erityisesti nämä asiat huomioon hakiessasi tietoa lähdemateriaalista: ${materialHandlingInstructions.trim()}`;
			enhancedPrompt += instructionsContext;
		}

		return enhancedPrompt;
	}
}
